import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Pagination } from "antd";
import { SaveOutlined, PlusOutlined, HomeOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useCreateMedicationsMutation, useGetmedicationSupplementQuery } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";
import { useDispatch } from "react-redux";
import { tryEarnUserAction } from "../../utils/gamificationService";
import { hydrateGamification } from "../../utils/gamificationSlice";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";



const generateKey = () => `${Date.now()}-${Math.random()}`;
/** Only these times appear in each slot dropdown — invalid times can’t be chosen (no toast). */
const SLOT_TIME_STEP_MIN = 15;

function minutesToHHmm(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTimeOptionLabel(hhmm: string): string {
    const [hStr, mStr] = hhmm.split(":");
    const h24 = parseInt(hStr, 10);
    const minute = mStr || "00";
    if (Number.isNaN(h24)) return hhmm;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 || 12;
    return `${h12}:${minute} ${ampm}`;
}

const Medication = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const effectiveUser = useEffectiveUserData();
    const patientId = effectiveUser?.patientId || 14;
    const [medicationSearch, setMedicationSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [createMedicationSchedule] = useCreateMedicationsMutation();
    const { data: medicationResults } = useGetmedicationSupplementQuery(medicationSearch, {
        skip: medicationSearch.length === 1,
    });

    function buildSlotTimeOptions(minTotalMin: number, maxTotalMin: number): { value: string; label: string }[] {
        const options: { value: string; label: string }[] = [];

        for (let t = minTotalMin; t <= maxTotalMin; t += SLOT_TIME_STEP_MIN) {
            const value = minutesToHHmm(t);
            options.push({ value, label: formatTimeOptionLabel(value) });
        }

        // ── add the last :59 of the slot if not already included ──
        const lastValue = minutesToHHmm(maxTotalMin);
        if (!options.find(o => o.value === lastValue)) {
            options.push({ value: lastValue, label: formatTimeOptionLabel(lastValue) });
        }

        return options;
    }

    // ── now set max to :59 ──
    const morningTimeOptions = useMemo(() => buildSlotTimeOptions(0, 11 * 60 + 59), []);  // 12:00 AM – 11:59 AM

    const noonTimeOptions = useMemo(() => buildSlotTimeOptions(12 * 60, 15 * 60 + 59), []);  // 12:00 PM – 3:59 PM

    const eveningTimeOptions = useMemo(() => buildSlotTimeOptions(16 * 60, 19 * 60 + 59), []);  // 4:00 PM – 7:59 PM

    const nightTimeOptions = useMemo(() => buildSlotTimeOptions(20 * 60, 23 * 60 + 59), []);  // 8:00 PM – 11:59 PM

    const timeTest = (slot: string, flag: string) =>
        Yup.string().test(
            `${slot}-time-required`,
            `${slot} time is required`,
            function (value) {
                return !this.parent[flag] || !!value;
            }
        );

    const dosageTest = (slot: string, flag: string) =>
        Yup.string().test(
            `${slot}-dosage-required`,
            `${slot} dosage is required`,
            function (value) {
                if (!this.parent[flag]) return true;  // unchecked → always valid
                if (!value) return false;             // checked but empty

                // ── inline slotDosageSchema rules ──
                if (isNaN(Number(value)))
                    return this.createError({ message: `${slot} dosage must be a number` });
                if (Number(value) <= 0)
                    return this.createError({ message: `${slot} dosage must be greater than 0` });

                return true;
            }
        );

    const validationSchema = Yup.object()
        .shape({
            medicationId: Yup.string()
                .transform((v) => (v == null || v === "" ? "" : String(v)))
                .required("Medication name is required"),
            medicationType: Yup.string().required("Type is required"),
            frequencyPerDay: Yup.number()
                .min(1, "Minimum 1")
                .max(4, "Maximum 4")
                .required("Frequency is required"),
            startDate: Yup.string().required("Start date is required"),
            endDate: Yup.string()
                .required("End date is required")
                .test(
                    "end-after-start",
                    "End date must be on or after start date",
                    function (endVal) {
                        const startVal = this.parent.startDate;
                        if (!startVal || !endVal) return true;
                        return String(endVal) >= String(startVal);
                    }
                ),
            morningTime: timeTest("Morning", "morning"),
            morningDosage: dosageTest("Morning", "morning"),

            noonTime: timeTest("Noon", "noon"),
            noonDosage: dosageTest("Noon", "noon"),

            eveningTime: timeTest("Evening", "evening"),
            eveningDosage: dosageTest("Evening", "evening"),

            nightTime: timeTest("Night", "night"),
            nightDosage: dosageTest("Night", "night"),
        })
        .test("slots", function (values: any) {
            const n = [values.morning, values.noon, values.evening, values.night].filter(Boolean).length;
            const f = Number(values.frequencyPerDay);
            if (n === 0) {
                return this.createError({
                    path: "frequencyPerDay",
                    message:
                        "Select at least one time slot (Morning, Noon, Evening, or Night).",
                });
            }
            if (Number.isFinite(f) && n !== f) {
                return this.createError({
                    path: "frequencyPerDay",
                    message: `Frequency is ${f} per day — select exactly ${f} time slot(s) (currently ${n} selected).`,
                });
            }
            return true;
        });
    // ── add these states ──
    const [editingMedicationId, setEditingMedicationId] = useState<string | null>(null);
    const formatTimeWithAmPm = (time: any) => {
        if (time == null || typeof time !== "string") return null;
        const raw = time.trim();
        if (!raw) return null;
        const parts = raw.split(":");
        if (parts.length < 2) return null;
        let hours = Number(parts[0]);
        let minutes = Number(parts[1]);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        hours = ((hours % 24) + 24) % 24;
        minutes = Math.min(59, Math.max(0, minutes));
        const ampm = hours >= 12 ? "PM" : "AM";
        let h12 = hours % 12;
        if (h12 === 0) h12 = 12;
        return `${String(h12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
    };
    const formik = useFormik({

        initialValues: {

            medicationId: "",
            medicationName: "",
            medicationType: "tablet",
            frequencyPerDay: 1,
            startDate: "",
            endDate: "",

            morning: false,
            noon: false,
            evening: false,
            night: false,

            morningTime: "",
            morningDosage: "",
            morningMeal: "After Meal",

            noonTime: "",
            noonDosage: "",
            noonMeal: "After Meal",

            eveningTime: "",
            eveningDosage: "",
            eveningMeal: "After Meal",

            nightTime: "",
            nightDosage: "",
            nightMeal: "After Meal",

            additionalNotes: ""

        },

        validationSchema,

        onSubmit: (values) => {
            const slotPayload = (enabled: boolean, timeVal: string, dosageVal: string, meal: string) => {
                if (!enabled) return null;
                const t = formatTimeWithAmPm(timeVal);
                const d = parseFloat(String(dosageVal));
                if (!t || !Number.isFinite(d)) {
                    toast.error("Please fix time and quantity for each selected slot.");
                    return null;
                }
                return { time: t, dosage: d, mealInstruction: meal };
            };

            const morning = slotPayload(values.morning, values.morningTime, values.morningDosage, values.morningMeal);
            const noon = slotPayload(values.noon, values.noonTime, values.noonDosage, values.noonMeal);
            const evening = slotPayload(values.evening, values.eveningTime, values.eveningDosage, values.eveningMeal);
            const night = slotPayload(values.night, values.nightTime, values.nightDosage, values.nightMeal);

            if (values.morning && !morning) return;
            if (values.noon && !noon) return;
            if (values.evening && !evening) return;
            if (values.night && !night) return;

            const updatedMed = {
                medicationId: values.medicationId,
                medicationName: values.medicationName,
                medicationType: values.medicationType,
                frequencyPerDay: Number(values.frequencyPerDay),
                intakeTime: { morning, noon, evening, night },
                additionalNotes: values.additionalNotes?.trim() || "",
            };

            // ── EDIT MODE ──
            if (editingMedicationId) {
                setMedications((prev: any) =>
                    prev.map((item: any) => ({
                        ...item,
                        startDate: values.startDate,
                        endDate: values.endDate,
                        medications: item.medications.map((m: any) =>
                            m.uniqueKey === editingMedicationId  // ← uniqueKey not medicationId
                                ? { ...updatedMed, uniqueKey: editingMedicationId } // ← preserve uniqueKey
                                : m
                        ),
                    }))
                );
                setEditingMedicationId(null);
                toast.success("Medicine updated successfully");

                // ── ADD MODE ──
            } else {
                const medicine = {
                    startDate: values.startDate,
                    endDate: values.endDate,
                    medications: [{
                        ...updatedMed,
                        uniqueKey: generateKey(), // ← add unique key
                    }],
                };
                setMedications((prev: any) => [...prev, medicine]);
                toast.success("Medicine added successfully");
            }

            formik.resetForm();
        },
    });

    const handleEdit = (record: any) => {
        setEditingMedicationId(record.uniqueKey);

        // ── find the parent schedule for dates ──
        const parentSchedule = medications.find((item: any) =>
            item.medications.some((m: any) => m.uniqueKey === record.uniqueKey)
        );

        // ── pre-fill formik ──
        formik.setValues({
            medicationId: String(record.medicationId),
            medicationName: record.medicationName,
            medicationType: record.medicationType,
            frequencyPerDay: record.frequencyPerDay,
            startDate: parentSchedule?.startDate || "",
            endDate: parentSchedule?.endDate || "",

            morning: !!record.intakeTime?.morning,
            noon: !!record.intakeTime?.noon,
            evening: !!record.intakeTime?.evening,
            night: !!record.intakeTime?.night,

            morningTime: record.intakeTime?.morning?.time?.slice(0, 5) || "",
            morningDosage: String(record.intakeTime?.morning?.dosage || ""),
            morningMeal: record.intakeTime?.morning?.mealInstruction || "After Meal",

            noonTime: record.intakeTime?.noon?.time?.slice(0, 5) || "",
            noonDosage: String(record.intakeTime?.noon?.dosage || ""),
            noonMeal: record.intakeTime?.noon?.mealInstruction || "After Meal",

            eveningTime: record.intakeTime?.evening?.time?.slice(0, 5) || "",
            eveningDosage: String(record.intakeTime?.evening?.dosage || ""),
            eveningMeal: record.intakeTime?.evening?.mealInstruction || "After Meal",

            nightTime: record.intakeTime?.night?.time?.slice(0, 5) || "",
            nightDosage: String(record.intakeTime?.night?.dosage || ""),
            nightMeal: record.intakeTime?.night?.mealInstruction || "After Meal",

            additionalNotes: record.additionalNotes || "",
        });

        // ── scroll to form ──
        window.scrollTo({ top: 0, behavior: "smooth" });
    };


    const [medications, setMedications] = useState<any>([]);


    const [selectedMedications, setSelectedMedications] = useState<any>([]);
    const medicationApiOptions =
        medicationResults?.data?.map((item: any) => ({
            id: item.id,
            name: item.name,
            descriptions: item.name,
        })) || [];

    const mergeOptions = (selected: any = null, apiOptions: any[] = []) => {
        const map = new Map();

        const selectedArray = selected ? [selected] : [];

        const keyOf = (item: any) => {
            const id = item?.id ?? item?.value ?? item?.name;
            return id === undefined || id === null || id === "" ? null : String(id).trim().toUpperCase();
        };

        [...selectedArray, ...apiOptions].forEach((item) => {
            const key = keyOf(item);
            if (!key) return;
            const normalized = { ...item, id: item?.id ?? item?.name ?? key };
            map.set(key, normalized);
        });

        return Array.from(map.values());
    };
    const medicationOptions = mergeOptions(selectedMedications, medicationApiOptions);


    const handleFinalSave = async () => {

        if (medications.length === 0) {
            toast.error("Please add at least one medication");
            return;
        }



        const payload = {
            "patientId": patientId,
            medicationSchedule:
                medications

        };

        console.log("Final Payload", payload);

        try {

            const res = await createMedicationSchedule(payload).unwrap();

            toast.success(res?.message || "Medication saved successfully");

            const uid = effectiveUser?.userId ?? effectiveUser?.id;
            if (uid) {
                const coin = tryEarnUserAction(String(uid), "medication_logged");
                if (coin.ok) {
                    dispatch(hydrateGamification(String(uid)));
                }
            }

            navigate("/medication");

        } catch (error: any) {

            // toast.error(error?.data?.message || "Failed to save medication");

        }

    };

    const columns = [
        { title: "Medicine", dataIndex: "medicationName" },
        { title: "Type", dataIndex: "medicationType" },
        { title: "Frequency", dataIndex: "frequencyPerDay" },
        {
            title: "Times",
            render: (record: any) => {
                const times = [];
                if (record.intakeTime?.morning) times.push(`Morning ${record.intakeTime.morning.time}`);
                if (record.intakeTime?.noon) times.push(`Noon ${record.intakeTime.noon.time}`);
                if (record.intakeTime?.evening) times.push(`Evening ${record.intakeTime.evening.time}`);
                if (record.intakeTime?.night) times.push(`Night ${record.intakeTime.night.time}`);
                return times.join(", ");
            }
        },
        {
            title: "Action",
            render: (record: any) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(record)}   // ← wired
                    >
                        Edit
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(record.uniqueKey)}
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];
    const flatMedications = useMemo(
        () => medications.flatMap((item: any) => item.medications),
        [medications]
    );
    const paginatedMedications = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return flatMedications.slice(start, start + pageSize);
    }, [flatMedications, currentPage, pageSize]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(flatMedications.length / pageSize));
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [flatMedications.length, pageSize, currentPage]);



    const selectStyles = {
        control: (base: any) => ({
            ...base,
            minHeight: "38px",
            borderColor: "#ced4da",
            boxShadow: "none",
            "&:hover": { borderColor: "#86b7fe" },
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    };


    const handleDelete = (id: any) => {

        const updated = medications
            .map((item: any) => ({
                ...item,
                medications: item.medications.filter((med: any) => med.uniqueKey !== id)
            }))
            .filter((item: any) => item.medications.length > 0);

        setMedications(updated);
    };


    return (

        <div className="content-wrapper">

            <div className="container-fluid">

                <div className="page-header">

                    <div className="breadcrumb">
                        <h3 className="page-title">Medication</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                            <span className="separator"> - </span>
                            <span className="current">Add Medication</span>
                        </span>
                    </div>

                </div>

                <section className="content">

                    <div className="row">

                        <div className="col-lg-12 col-12">

                            <div className="box">

                                <div className="box-header with-border mb-2">
                                    <h5 className="box-title">Add Medication</h5>
                                </div>

                                <form className="box-body mt-2" style={{ padding: "0 3.5rem 3rem 2rem" }} onSubmit={formik.handleSubmit}>
                                    <h5 className="box-title inner-header-form mb-0"><i className="ti-pencil-alt me-15"></i>
                                        Medication Info</h5>
                                    <hr className="my-10"></hr>
                                    <div className="row g-3">

                                        <div className="col-12">
                                            <label className="form-label">Medication Names</label>

                                            <Select
                                                styles={{
                                                    ...selectStyles,
                                                    menuPortal: (base: any) => ({
                                                        ...base,
                                                        zIndex: 9999
                                                    }),
                                                }}
                                                classNamePrefix="react-select"
                                                onInputChange={(val: any) => setMedicationSearch(val)}
                                                options={medicationOptions}
                                                getOptionLabel={(option: any) => option.name}
                                                getOptionValue={(option: any) => option.id}

                                                value={
                                                    medicationOptions.find(
                                                        (opt: any) =>
                                                            String(opt.id) ===
                                                            String(formik.values.medicationId)
                                                    ) || null
                                                }

                                                onChange={(val: any) => {
                                                    formik.setFieldValue(
                                                        "medicationId",
                                                        val?.id != null ? String(val.id) : ""
                                                    );
                                                    formik.setFieldValue("medicationName", val?.name || "");
                                                    formik.setFieldTouched("medicationId", true);
                                                }}
                                                onBlur={() => formik.setFieldTouched("medicationId", true)}

                                                placeholder="Medication name"
                                                menuPortalTarget={document.body}

                                                formatOptionLabel={(option: any) => (
                                                    <span title={option.descriptions}>
                                                        {option.descriptions}
                                                    </span>
                                                )}
                                            />

                                            {formik.touched.medicationId &&
                                                formik.values.medicationId === "" &&
                                                formik.errors.medicationId && (
                                                    <p className="text-danger mt-1 mb-0 fs-12">
                                                        {formik.errors.medicationId as string}
                                                    </p>
                                                )}
                                        </div>
                                        {/* <div className="col-md-6">
                                            <label className="form-label">Cause of Medication</label>
                                            <input name="causeofMedicaation" className="form-control" style={{ fontSize: "1.2rem" }} />

                                        </div> */}
                                        <h5 className="box-title inner-header-form mb-0 mt-20"><i
                                            className="ti-timer me-15"></i> Dosage & Schedule</h5>
                                        <hr className="my-10"></hr>

                                        <div className="col-lg-3 col-md-6">
                                            <label>Type</label>
                                            <select
                                                name="medicationType"
                                                className="form-control mt-2"
                                                style={{ backgroundColor: "#fff" }}
                                                value={formik.values.medicationType}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option value="tablet">Tablet</option>
                                                <option value="capsule">Capsule</option>
                                                <option value="syrup">Syrup</option>
                                            </select>

                                            {formik.touched.medicationType && formik.errors.medicationType && (
                                                <p className="text-danger mt-1 mb-0 fs-12">
                                                    {formik.errors.medicationType}
                                                </p>
                                            )}
                                        </div>

                                        <div className="col-lg-3 col-md-6">
                                            <label>Frequency</label>
                                            <select
                                                name="frequencyPerDay"
                                                className="form-control mt-2"
                                                style={{ backgroundColor: "#fff" }}
                                                value={formik.values.frequencyPerDay}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option value={1}>1</option>
                                                <option value={2}>2</option>
                                                <option value={3}>3</option>
                                                <option value={4}>4</option>
                                            </select>


                                        </div>

                                        <div className="col-lg-3 col-md-6">
                                            <label>Start Date</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                className="form-control mt-2"
                                                value={formik.values.startDate}
                                                onChange={(e) => {
                                                    formik.handleChange(e);


                                                    if (
                                                        formik.values.endDate &&
                                                        e.target.value &&
                                                        formik.values.endDate < e.target.value
                                                    ) {
                                                        formik.setFieldValue("endDate", "");
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                                max={
                                                    formik.values.endDate ? formik.values.endDate : ''
                                                }
                                                min={new Date().toISOString().split("T")[0]}
                                            />

                                            {formik.touched.startDate && formik.errors.startDate && (
                                                <p className="text-danger mt-1 mb-0 fs-12">
                                                    {formik.errors.startDate}
                                                </p>
                                            )}
                                        </div>

                                        <div className="col-lg-3 col-md-6">
                                            <label>End Date</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                className="form-control mt-2"
                                                value={formik.values.endDate}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                min={formik.values.startDate || ""}
                                                // max={new Date().toISOString().split("T")[0]}
                                                disabled={!formik.values.startDate}
                                            />

                                            {formik.touched.endDate && formik.errors.endDate && (
                                                <p className="text-danger mt-1 mb-0 fs-12">
                                                    {formik.errors.endDate}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {/* Morning + Noon */}
                                    <div className="row mt-3">

                                        {/* Morning */}
                                        <div className="col-md-6 custom-toggle">
                                            <label className="fw-bold mb-2">
                                                <input
                                                    type="checkbox"

                                                    className="me-2 hidden-checkbox"
                                                    style={{
                                                        position: 'relative',
                                                        left: 0,
                                                        opacity: 1000
                                                    }}
                                                    checked={formik.values.morning}
                                                    onChange={() => {
                                                        const next = !formik.values.morning;
                                                        formik.setFieldValue("morning", next);
                                                        if (!next) {
                                                            formik.setFieldValue("morningTime", "");
                                                            formik.setFieldValue("morningDosage", "");
                                                            formik.setFieldValue("morningMeal", "After Meal");


                                                            formik.setFieldTouched("morningTime", false);    // ← add this
                                                            formik.setFieldTouched("morningDosage", false);  // ← add this
                                                            formik.setFieldError("morningTime", undefined);
                                                            formik.setFieldError("morningDosage", undefined);
                                                        }
                                                    }}
                                                />
                                                Morning
                                            </label>

                                            <div className="row">
                                                <div className="col-md-3">
                                                    <select
                                                        name="morningMeal"
                                                        className={`form-control ${!formik.values.morning ? 'disabled-field' : 'enabled-field'}`}

                                                        value={formik.values.morningMeal}
                                                        disabled={!formik.values.morning}
                                                        onChange={formik.handleChange}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any, // use 'as any' to bypass TS issue
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,



                                                        }}
                                                    >
                                                        <option value="Before Meal">Before Meal</option>
                                                        <option value="After Meal">After Meal</option>
                                                    </select>
                                                    {formik.touched.morningMeal && formik.errors.morningMeal && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.morningMeal}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="col-md-3">
                                                    <select
                                                        name="morningTime"
                                                        className={`form-control ${!formik.values.morning ? 'disabled-field' : 'enabled-field'}`}
                                                        value={formik.values.morningTime}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={!formik.values.morning}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any,
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,

                                                        }}
                                                    >
                                                        <option value="">Select time</option>
                                                        {morningTimeOptions.map((o) => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.touched.morningTime && formik.errors.morningTime && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.morningTime}
                                                        </p>
                                                    )}
                                                </div>


                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        name="morningDosage"
                                                        className="form-control"
                                                        placeholder="Quantity"
                                                        value={formik.values.morningDosage}
                                                        disabled={!formik.values.morning}
                                                        onChange={formik.handleChange}
                                                    />
                                                    {formik.touched.morningDosage && formik.errors.morningDosage && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.morningDosage}
                                                        </p>
                                                    )}
                                                </div>


                                            </div>
                                        </div>


                                        {/* Noon */}
                                        <div className="col-md-6">
                                            <label className="fw-bold mb-2">
                                                <input
                                                    type="checkbox"

                                                    className="me-2 hidden-checkbox"
                                                    style={{
                                                        position: 'relative',
                                                        left: 0,
                                                        opacity: 1000
                                                    }}
                                                    checked={formik.values.noon}
                                                    onChange={() => {
                                                        const next = !formik.values.noon;
                                                        formik.setFieldValue("noon", next);
                                                        if (!next) {
                                                            formik.setFieldValue("noonTime", "");
                                                            formik.setFieldValue("noonDosage", "");
                                                            formik.setFieldValue("noonMeal", "After Meal");


                                                            formik.setFieldTouched("noonTime", false);    // ← add this
                                                            formik.setFieldTouched("noonDosage", false);  // ← add this
                                                            formik.setFieldError("noonTime", undefined);
                                                            formik.setFieldError("noonDosage", undefined);
                                                        }
                                                    }}
                                                />
                                                Noon
                                            </label>

                                            <div className="row">
                                                <div className="col-md-3">
                                                    <select
                                                        name="noonMeal"
                                                        className={`form-control ${!formik.values.noon ? 'disabled-field' : 'enabled-field'}`}

                                                        value={formik.values.noonMeal}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.noon}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any, // use 'as any' to bypass TS issue
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,



                                                        }}
                                                    >
                                                        <option value="Before Meal">Before Meal</option>
                                                        <option value="After Meal">After Meal</option>
                                                    </select>
                                                    {formik.touched.noonMeal && formik.errors.noonMeal && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.noonMeal}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="col-md-3">
                                                    <select
                                                        name="noonTime"
                                                        className={`form-control ${!formik.values.noon ? 'disabled-field' : 'enabled-field'}`}
                                                        value={formik.values.noonTime}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={!formik.values.noon}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any,
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,
                                                        }}
                                                    >
                                                        <option value="">Select time</option>
                                                        {noonTimeOptions.map((o) => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.touched.noonTime && formik.errors.noonTime && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.noonTime}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        name="noonDosage"
                                                        className="form-control"
                                                        placeholder="Quantity"
                                                        value={formik.values.noonDosage}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.noon}
                                                    />
                                                    {formik.touched.noonDosage && formik.errors.noonDosage && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.noonDosage}
                                                        </p>
                                                    )}
                                                </div>


                                            </div>
                                        </div>

                                    </div>


                                    {/* Evening + Night */}
                                    <div className="row mt-3">

                                        {/* Evening */}
                                        <div className="col-md-6">
                                            <label className="fw-bold mb-2">
                                                <input
                                                    type="checkbox"

                                                    className="me-2 hidden-checkbox"
                                                    style={{
                                                        position: 'relative',
                                                        left: 0,
                                                        opacity: 1000
                                                    }}
                                                    checked={formik.values.evening}
                                                    onChange={() => {
                                                        const next = !formik.values.evening;
                                                        formik.setFieldValue("evening", next);
                                                        if (!next) {
                                                            formik.setFieldValue("eveningTime", "");
                                                            formik.setFieldValue("eveningDosage", "");
                                                            formik.setFieldValue("eveningMeal", "After Meal");
                                                            formik.setFieldTouched("eveningTime", false);    // ← add this
                                                            formik.setFieldTouched("eveningDosage", false);  // ← add this
                                                            formik.setFieldError("eveningTime", undefined);
                                                            formik.setFieldError("eveningDosage", undefined);
                                                        }
                                                    }}
                                                />
                                                Evening
                                            </label>

                                            <div className="row">
                                                <div className="col-md-3">
                                                    <select
                                                        name="eveningMeal"
                                                        style={{

                                                            WebkitAppearance: "menulist" as any, // use 'as any' to bypass TS issue
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,



                                                        }}
                                                        className={`form-control ${!formik.values.evening ? 'disabled-field' : 'enabled-field'}`}
                                                        // style={{ backgroundColor: "#fff" }}
                                                        value={formik.values.eveningMeal}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.evening}
                                                    >
                                                        <option value="Before Meal">Before Meal</option>
                                                        <option value="After Meal">After Meal</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <select
                                                        name="eveningTime"
                                                        className={`form-control ${!formik.values.evening ? 'disabled-field' : 'enabled-field'}`}
                                                        value={formik.values.eveningTime}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={!formik.values.evening}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any,
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,
                                                        }}
                                                    >
                                                        <option value="">Select time</option>
                                                        {eveningTimeOptions.map((o) => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.touched.eveningTime && formik.errors.eveningTime && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.eveningTime}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        name="eveningDosage"
                                                        className="form-control"
                                                        placeholder="Quantity"
                                                        value={formik.values.eveningDosage}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.evening}
                                                    />
                                                    {formik.touched.eveningDosage && formik.errors.eveningDosage && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.eveningDosage}
                                                        </p>
                                                    )}
                                                </div>


                                            </div>
                                        </div>


                                        {/* Night */}
                                        <div className="col-md-6">
                                            <label className="fw-bold mb-2">
                                                <input
                                                    type="checkbox"

                                                    className="me-2 hidden-checkbox"
                                                    style={{
                                                        position: 'relative',
                                                        left: 0,
                                                        opacity: 1000
                                                    }}
                                                    checked={formik.values.night}
                                                    onChange={() => {
                                                        const next = !formik.values.night;
                                                        formik.setFieldValue("night", next);
                                                        if (!next) {
                                                            formik.setFieldValue("nightTime", "");
                                                            formik.setFieldValue("nightDosage", "");
                                                            formik.setFieldValue("nightMeal", "After Meal");
                                                            formik.setFieldTouched("nightTime", false);    // ← add this
                                                            formik.setFieldTouched("nightDosage", false);  // ← add this
                                                            formik.setFieldError("nightTime", undefined);
                                                            formik.setFieldError("nightDosage", undefined);
                                                        }
                                                    }}
                                                />
                                                Night
                                            </label>

                                            <div className="row">
                                                <div className="col-md-3">
                                                    <select
                                                        name="nightMeal"
                                                        className={`form-control ${!formik.values.night ? 'disabled-field' : 'enabled-field'}`}

                                                        value={formik.values.nightMeal}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.night}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any, // use 'as any' to bypass TS issue
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,



                                                        }}
                                                    >
                                                        <option value="Before Meal">Before Meal</option>
                                                        <option value="After Meal">After Meal</option>
                                                    </select>
                                                    {formik.touched.nightMeal && formik.errors.nightMeal && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.nightMeal}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="col-md-3">
                                                    <select
                                                        name="nightTime"
                                                        className={`form-control ${!formik.values.night ? 'disabled-field' : 'enabled-field'}`}
                                                        value={formik.values.nightTime}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={!formik.values.night}
                                                        style={{

                                                            WebkitAppearance: "menulist" as any,
                                                            MozAppearance: "menulist" as any,
                                                            appearance: "menulist" as any,
                                                        }}
                                                    >
                                                        <option value="">Select time</option>
                                                        {nightTimeOptions.map((o) => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.touched.nightTime && formik.errors.nightTime && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.nightTime}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        name="nightDosage"
                                                        className="form-control"
                                                        placeholder="Quantity"
                                                        value={formik.values.nightDosage}
                                                        onChange={formik.handleChange}
                                                        disabled={!formik.values.night}
                                                    />
                                                    {formik.touched.nightDosage && formik.errors.nightDosage && (
                                                        <p className="text-danger mt-1 mb-0 fs-12">
                                                            {formik.errors.nightDosage}
                                                        </p>
                                                    )}
                                                </div>


                                            </div>

                                        </div>

                                    </div>

                                    <div className="row g-3 mt-2">

                                        {(formik.touched.frequencyPerDay || formik.submitCount > 0) &&
                                            formik.errors.frequencyPerDay && (
                                                <p className="text-danger mt-1 mb-0 fs-12">
                                                    {formik.errors.frequencyPerDay as string}
                                                </p>
                                            )}



                                        <h5 className="box-title inner-header-form mb-0 mt-20"><i
                                            className="ti-timer me-15"></i> Additional Notes</h5>
                                        <hr className="my-10"></hr>

                                        <div className="col-12">
                                            <label>Additional Notes</label>
                                            <textarea
                                                name="additionalNotes"
                                                className="form-control mt-2"
                                                value={formik.values.additionalNotes}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                rows={5}
                                                placeholder="Enter Additional Notes"
                                            />
                                        </div>





                                    </div>

                                    <div className="mt-3">
                                        <button type="submit" className="btn btn-success">
                                            {editingMedicationId
                                                ? <><SaveOutlined /> Update Medicine</>
                                                : <><PlusOutlined /> Add Medicine</>
                                            }
                                        </button>
                                        {editingMedicationId && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary ms-2"
                                                onClick={() => {
                                                    setEditingMedicationId(null);
                                                    formik.resetForm();
                                                }}
                                            >
                                                Cancel Edit
                                            </button>
                                        )}

                                    </div>


                                </form>

                            </div>

                        </div>

                    </div>

                </section>

                {medications.length > 0 && (
                    <>
                        <div className="box m-4">

                            <div className="box-header with-border">
                                <h4 className="box-title">
                                    <i className="ti-list me-10"></i>
                                    Added Medications
                                </h4>
                            </div>

                            <div className="box-body">
                                <Table
                                    columns={columns}
                                    dataSource={paginatedMedications}
                                    rowKey="uniqueKey"
                                    pagination={false}
                                />
                                <div className="family-footer">
                                    <span>Total {flatMedications.length} Medications</span>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={flatMedications.length}
                                        showSizeChanger
                                        pageSizeOptions={["5", "10", "20", "50"]}
                                        onChange={(page, size) => {
                                            setCurrentPage(page);
                                            setPageSize(size);
                                        }}
                                        onShowSizeChange={(_, size) => {
                                            setCurrentPage(1);
                                            setPageSize(size);
                                        }}
                                    />
                                </div>
                            </div>

                        </div>

                        <div className="m-4">
                            <button className="btn btn-primary" onClick={handleFinalSave}>
                                <SaveOutlined /> Save All Medications
                            </button>
                        </div>
                    </>
                )}

            </div>

        </div>

    );
};

export default Medication;